/**
 * Roles ↔ surfaces mapping. Surfaces are the top-level UI contexts a user can
 * enter (`/app`, `/driver`, `/ops`); a role lists which surfaces it can reach.
 *
 * Authorization model:
 * - Role gates every surface; selected staff surfaces also require one of the
 *   privileges declared in this file
 * - Privileges gate tabs/pages/procedures inside `/ops`
 * - `superadmin` is a role; it bypasses every privilege check via middleware
 */
// Re-exported from the single source in this package, which the DB enum is
// also built from — never re-declared here.
import type { AccessPrivilege } from './access-privilege'
import { SUPERADMIN_ROLE, type UserRole } from './user-role'

export { SUPERADMIN_ROLE, USER_ROLES } from './user-role'
export type { UserRole }
export type Surface = 'ops' | 'app' | 'driver' | 'logistics' | 'studio'

/**
 * The single source of truth for "is this caller a platform superadmin?".
 * Read by the web's gates and by the API's privilege bypass alike — which is
 * why it lives here rather than beside either.
 */
export function isSuperadmin(role: string | null | undefined): boolean {
  return role === SUPERADMIN_ROLE
}

/**
 * Narrow an untrusted session value to a known role.
 *
 * `hasOwn`, not `in`: `in` walks the prototype chain, so 'constructor' and
 * 'toString' would pass and then index the map to something truthy-but-wrong.
 */
export function isUserRole(role: string | null | undefined): role is UserRole {
  return role != null && Object.hasOwn(ROLE_SURFACES, role)
}

/**
 * Which surfaces each role can access.
 *
 * `Record<UserRole, …>` is load-bearing: a role added to the taxonomy without
 * an entry here fails the build rather than silently reaching nothing.
 *
 * **The `driver` row is a SERVER AUTHORIZATION set, not only navigation.**
 * `SURFACE_ROLES.driver` is what decides who may be handed a delivery run and
 * therefore read a customer's name and address on the driver surface. A row
 * edited for a purely visual reason — "let warehouse staff preview the driver
 * screen" — widens that too, from a diff containing no API file, which is why
 * `driver-surface-authz.test.ts` pins this row from the API package, where the
 * consequence lands.
 *
 * Both maps are frozen below. `readonly` is a COMPILE-TIME check only — an
 * type alone does not make the set untamperable at runtime.
 */
export const ROLE_SURFACES: Record<UserRole, readonly Surface[]> = {
  customer: ['app'],
  // A business customer reaches the customer surface and nothing else. Which
  // PRODUCT `/app` then serves is decided by `resolveAppProduct`, not here:
  // this map answers "may this role enter the surface", and business and
  // consumer customers share it. Widening it to a 'business' surface would
  // make every `/app` route need a second registration for no gain.
  business: ['app'],
  driver: ['app', 'driver'],
  // Studio is listed for staff, but reaching a page still needs
  // `access_documents` — this map answers "may this role enter the surface",
  // and the privilege gate on every procedure is what decides the rest.
  employee: ['app', 'ops', 'logistics', 'studio'],
  superadmin: ['app', 'driver', 'ops', 'logistics', 'studio'],
}
for (const role of Object.keys(ROLE_SURFACES) as UserRole[]) {
  Object.freeze(ROLE_SURFACES[role])
}
Object.freeze(ROLE_SURFACES)

/** Derived: which roles can access each surface. */
export const SURFACE_ROLES: Record<Surface, readonly UserRole[]> = (() => {
  const out: Record<Surface, UserRole[]> = {
    app: [],
    ops: [],
    driver: [],
    logistics: [],
    studio: [],
  }
  const entries = Object.entries(ROLE_SURFACES) as [
    UserRole,
    readonly Surface[],
  ][]
  for (const [role, surfaces] of entries) {
    for (const surface of surfaces) {
      out[surface].push(role)
    }
  }
  // Frozen for the same reason as the source map above: these are module
  // singletons every importer shares, and `driver` is read live on every
  // dispatch, so one `.push()` anywhere would widen a SQL `WHERE` for the life
  // of the process.
  for (const surface of Object.keys(out) as Surface[]) {
    Object.freeze(out[surface])
  }
  return Object.freeze(out)
})()

/**
 * Privileges that admit a role to a privilege-confined surface. Holding any
 * listed privilege is enough; superadmin bypasses this axis.
 *
 * `ops` deliberately has no entry: it hosts Operations, Maestro, Admin and
 * Dev, and leaf tab gates confine what a staffer can open after entering it.
 * `app` deliberately has no entry because a staff member may also legitimately
 * be a customer; removing that product access is a product decision, not a
 * security decision.
 */
export const SURFACE_PRIVILEGES: Partial<
  Record<Surface, readonly AccessPrivilege[]>
> = Object.freeze({
  logistics: Object.freeze([
    'access_warehouses',
    'access_dashboard',
    'access_dispatch',
    'access_trackers',
    'access_print_queue',
    'access_scan_overrides',
  ] as const),
  studio: Object.freeze(['access_documents'] as const),
})
